import React, { useEffect, useRef, useState } from "react";
import { Play, X, Volume2, VolumeX } from "lucide-react";

/**
 * ShowcaseVideo
 * - Renders a muted, looped, in-viewport preview (like a reel).
 * - Click expands into a fullscreen lightbox with audio + scrub controls.
 * - Source priority: youtubeId → vimeoId → src (mp4).
 */
const ShowcaseVideo = ({ item }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState(true);

  // Auto-play preview only when card is in viewport (perf-friendly).
  useEffect(() => {
    if (item.youtubeId || item.vimeoId) return;
    const el = videoRef.current;
    const wrap = containerRef.current;
    if (!el || !wrap) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (open) return;
        if (entry.isIntersecting) {
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.3 }
    );
    io.observe(wrap);
    return () => io.disconnect();
  }, [open, item.youtubeId, item.vimeoId]);

  // Pause preview while lightbox is open.
  useEffect(() => {
    if (open && videoRef.current) videoRef.current.pause();
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const isEmbed = !!(item.youtubeId || item.vimeoId);

  return (
    <>
      <button
        type="button"
        ref={containerRef}
        onClick={() => setOpen(true)}
        data-testid={`showcase-video-open-${item.id}`}
        className="relative block w-full h-full overflow-hidden group bg-[#0b0b0d] cursor-pointer"
        aria-label={`Play ${item.title}`}
      >
        {isEmbed ? (
          <img
            src={
              item.thumbnail ||
              (item.youtubeId
                ? `https://img.youtube.com/vi/${item.youtubeId}/hqdefault.jpg`
                : "")
            }
            alt={item.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <video
            ref={videoRef}
            src={item.src}
            poster={item.thumbnail}
            muted
            loop
            playsInline
            preload="metadata"
            className="w-full h-full object-cover"
          />
        )}

        {/* Subtle gradient + play badge */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-black/10 pointer-events-none" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span
            className="w-16 h-16 rounded-full grid place-items-center shadow-lg transition-transform duration-300 group-hover:scale-110"
            style={{ background: "rgba(250, 246, 244, 0.95)" }}
          >
            <Play size={22} className="text-[var(--lt-charcoal)] translate-x-[2px]" fill="#1F1A17" />
          </span>
        </div>
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid={`showcase-video-modal-${item.id}`}
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center px-4 py-10"
          onClick={() => setOpen(false)}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid={`showcase-video-close-${item.id}`}
            className="absolute top-5 right-5 w-11 h-11 grid place-items-center bg-[var(--lt-peace)] text-[var(--lt-charcoal)] hover:bg-[var(--lt-clay)] transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>

          <div
            className="relative max-w-[min(900px,92vw)] max-h-[88vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {item.youtubeId ? (
              <div className="relative" style={{ paddingTop: "56.25%" }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${item.youtubeId}?autoplay=1`}
                  title={item.title}
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              </div>
            ) : item.vimeoId ? (
              <div className="relative" style={{ paddingTop: "56.25%" }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://player.vimeo.com/video/${item.vimeoId}?autoplay=1`}
                  title={item.title}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="relative bg-black">
                <video
                  src={item.src}
                  autoPlay
                  controls
                  muted={muted}
                  playsInline
                  className="w-full max-h-[80vh] object-contain bg-black"
                />
                <button
                  type="button"
                  onClick={() => setMuted((m) => !m)}
                  className="absolute bottom-4 right-4 w-11 h-11 grid place-items-center bg-[var(--lt-peace)]/90 text-[var(--lt-charcoal)] hover:bg-[var(--lt-peace)] transition-colors"
                  aria-label={muted ? "Unmute" : "Mute"}
                  data-testid={`showcase-video-mute-${item.id}`}
                >
                  {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
              </div>
            )}

            <div className="mt-4 text-[var(--lt-peace)]">
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--lt-clay)] mb-1">
                {item.client}
              </p>
              <h3 className="font-display text-2xl">{item.title}</h3>
              {item.description && (
                <p className="text-sm text-[var(--lt-clay)]/80 mt-1 max-w-2xl">
                  {item.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShowcaseVideo;
